// Angular
import {
	Component,
	Input,
	OnInit,
	ViewChild,
	ElementRef,
	OnChanges,
	SimpleChanges,
	OnDestroy
} from '@angular/core';

// Services
import { PhotoGalleryService } from './services/photo-gallery.service';

// Models
import { PhotoGalleryEvents } from './models/photo-gallery.events';
import { CustomAction, GalleryOptions } from './models/gallery-options.model';
import { ImageGallery } from './models/image-gallery.model';

// Others
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Lightbox, LightboxEvent, LIGHTBOX_EVENT } from 'ngx-lightbox';

@Component({
	selector: 'cv-gallery',
	templateUrl: './gallery.component.html',
	styleUrls: ['./gallery.component.scss']
})
export class GalleryComponent implements OnInit, OnChanges, OnDestroy {

	/*
	* Html references to apply styles dynamically
	*/

	@ViewChild('sceneContainer', { static: true }) sceneContainer: ElementRef<HTMLElement>;
	@ViewChild('galleryComponent', { static: true }) galleryComponent: ElementRef<HTMLElement>;

	/**
	 * Input with array of ImageGallery to use in Gallery
	 */
	@Input() album: ImageGallery[];

	/**
	 * Input with options of type GalleryOptions to use in gallery
	 */
	@Input() options: GalleryOptions;

	/**
	 * Input to add actions in gallery
	 */
	@Input() isEditable = true;

	currentAlbumImage: ImageGallery;
	showLoading = true;
	previewPhoto: string;
	currentIndexAlbumSelected: number;

	protected interval: number;
	imageErrorCounter: number;
	subscriptionLightBox: any;

	protected atComponentDestroy = new Subject();

	constructor(
		private lightBox: Lightbox,
		private lightboxEvent: LightboxEvent,
		private photoGalleryService: PhotoGalleryService,
	) {

		this.photoGalleryService
			.events
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((event: any) => {
				if (event.name === PhotoGalleryEvents.IMAGE_DELETED) {
					if (this.currentIndexAlbumSelected !== 0) {
						this.prev();
					} else {
						this.changeImage({ index: 0 });
					}
				}

				if (event.name === PhotoGalleryEvents.CHANGE_IMAGE) {
					if (this.currentIndexAlbumSelected != event.index) {
						this.changeImage({ index: this.currentIndexAlbumSelected });
					} else {
						this.changeImage({ index: 0 });
					}
				}
			});

	}

	ngOnInit(): void {
		this.imageErrorCounter = 0;
		this.sceneContainer.nativeElement.style.display = 'none';
		if ('size' in this.options && this.sceneContainer) {
			this.sceneContainer.nativeElement.style.width = this.options.size?.width;
			this.sceneContainer.nativeElement.style.height = this.options.size?.height;
		}

		this.changeImage({ index: 0 });
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.album && !changes.album.firstChange) {
			this.album = changes.album.currentValue;
			this.imageErrorCounter = 0;
			this.changeImage({ index: this.album[this.currentIndexAlbumSelected] ? this.currentIndexAlbumSelected : 0 });
		}
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * callback to open lightbox full size image
	 */
	openPreview() {
		if (this.options.showPreview) {
			this.lightBox.open(this.album, this.currentIndexAlbumSelected || 0);

			this.subscriptionLightBox = this.lightboxEvent.lightboxEvent$
				.subscribe((event: any) => {
					if (event.id === LIGHTBOX_EVENT.CLOSE) {
						this.subscriptionLightBox.unsubscribe();
					}

					if (event.id === LIGHTBOX_EVENT.OPEN) {
						// event OPEN is fired
					}

					if (event.id === LIGHTBOX_EVENT.CHANGE_PAGE) {
						this.changeImage({ index: event.data })
					}
				});
		}
	}

	/**
	 * send delete image event to the parent component
	 */
	deleteImage() {
		this.photoGalleryService.events.next({
			name: PhotoGalleryEvents.DELETE_IMAGE,
			item: this.currentAlbumImage,
			index: this.currentIndexAlbumSelected
		});
	}

	/**
	 * event to go to previous image in gallery
	 */
	prev() {
		if (this.currentIndexAlbumSelected === 0) {
			this.currentIndexAlbumSelected = this.album.length - 1;
		} else {
			this.currentIndexAlbumSelected--;
		}

		this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
		this.changeImage({ index: this.currentIndexAlbumSelected })
	}

	/**
	 * event to go to next image in gallery
	 */

	next() {
		this.currentIndexAlbumSelected++;
		if (this.album.length === this.currentIndexAlbumSelected) {
			this.currentIndexAlbumSelected = 0;
		}
		this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
		this.changeImage({ index: this.currentIndexAlbumSelected })
	}

	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.showLoading = false;
		this.sceneContainer.nativeElement.style.display = 'block';
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback when image is not loaded
	 */
	onImageError(): void {
		this.imageErrorCounter++;
		this.previewPhoto = null;
		if (this.interval) {
			clearInterval(this.interval);
		}
		this.showLoading = true;
		this.sceneContainer.nativeElement.style.display = 'none';

		if (this.imageErrorCounter < 10) {
			this.interval = window.setInterval(() => {
				this.previewPhoto = this.currentAlbumImage.thumb || this.currentAlbumImage.medium;
				this.imageErrorCounter++;
			}, 1000);
		} else {
			this.previewPhoto = this.options.noFilesImage;
			clearInterval(this.interval);
		}
	}

	/**
	 * Change image Callback
	 * @param data {index: int, img: string}
	 */
	changeImage(data?: any): void {
		if (this.album.length > 0) {
			this.currentIndexAlbumSelected = data.index;
			this.currentAlbumImage = this.album[this.currentIndexAlbumSelected];
			this.previewPhoto = this.currentAlbumImage.thumb || this.currentAlbumImage.medium;
		}
	}

	/**
	 * select image callback and send an event to the parent component
	 */
	selectImage() {
		this.changeImage({ index: this.currentIndexAlbumSelected });

		this.photoGalleryService.events.next({
			name: PhotoGalleryEvents.SELECT_IMAGE,
			item: this.currentAlbumImage,
			index: this.currentIndexAlbumSelected
		});
	}

	customActionEvent(action: CustomAction): void {
		this.photoGalleryService.events.next({
			name: action.eventName,
			item: this.currentAlbumImage,
			index: this.currentIndexAlbumSelected
		});
	}

}
